import { Box } from '@mantine/core';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { PortalHeader } from './PortalHeader';
import { PortalBottomNav } from './PortalBottomNav';
import { PortalIdleTimeoutGuard } from '../components/PortalIdleTimeoutGuard';

/**
 * Routes that opt out of the default 640 px narrow-shell width and use a
 * wider container so a two-pane (hero | chart+cards) layout has room to
 * breathe on laptop / FHD viewports.
 *
 * Per M1 polish round 3 (Fix P1) — the tax-timeline page targets:
 *   laptop-125 (1440 viewport) → ≥ 1200 px content
 *   FHD       (1920 viewport) → ≥ 1400 px content (capped at 1440 so it
 *                                does not become absurdly wide)
 * The shared portal shell at 640 px max-width is preserved for every other
 * route — opt-in keeps the blast radius scoped to one page.
 */
const WIDE_LAYOUT_PATH_PREFIXES = ['/portal/tax-timeline'];

export function PortalLayout() {
  const { isAuthenticated, _hasHydrated } = usePortalAuthStore();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const isWideLayout = WIDE_LAYOUT_PATH_PREFIXES.some((prefix) =>
    currentPath.startsWith(prefix),
  );

  useEffect(() => {
    if (_hasHydrated && !isAuthenticated) {
      void navigate({ to: '/portal/login' });
    }
  }, [isAuthenticated, _hasHydrated, navigate]);

  // Wait for store hydration before rendering
  if (!_hasHydrated) {
    return null;
  }

  // Don't render layout if not authenticated (redirect is pending)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        maxWidth: isWideLayout ? 1440 : 640,
        margin: '0 auto',
        backgroundColor: 'var(--mantine-color-body)',
      }}
    >
      <PortalIdleTimeoutGuard />
      <PortalHeader />

      {/* Scrollable content area */}
      <Box
        component='main'
        style={{
          flex: 1,
          overflow: 'auto',
          paddingBottom: 80, // Space for bottom nav (64px) + breathing room
        }}
      >
        <Outlet />
      </Box>

      <PortalBottomNav />
    </Box>
  );
}
